import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL
  ? process.env.REACT_APP_BACKEND_URL.replace(/^https?/, "wss").replace(/\/api$/, "")
  : "ws://localhost:8001";

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  const unreadCount = notifications.filter(n => !n.read).length;

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/notifications`);
      setNotifications(data);
    } catch (e) {
      console.error("Failed to fetch notifications", e);
    }
  }, [user]);

  const connectWs = useCallback(() => {
    if (!user) return;
    // Iter245 · Si no hay token en localStorage (sesión restaurada por
    // cookie httpOnly tras F5/deep-link), igualmente intentamos conectar:
    // el navegador manda la cookie automáticamente en el handshake WS y el
    // backend ya la acepta como fallback (ver server.py websocket_endpoint).
    const token = localStorage.getItem("valoriza_token") || "";
    const wsUrl = `${WS_BASE}/api/ws/${user.id}${token ? `?token=${token}` : ""}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log("Notification WS connected");
      reconnectAttemptsRef.current = 0;
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.event === "notification" && msg.data) {
          const notif = msg.data;
          setNotifications(prev => [notif, ...prev]);
          // Show toast based on type
          const icons = {
            offer_approved: "✓",
            offer_rejected: "✕",
            offer: "◎",
            workitem: "◈",
            sprint_delayed: "⚠",
            oferta_imported: "📥",
            auto_import_digest: "🤖",
          };
          const icon = icons[notif.type] || "•";
          toast(notif.message, {
            description: icon + " Nueva notificación",
            duration: 6000,
            action: notif.link ? {
              label: "Ver",
              onClick: () => { window.location.href = notif.link; }
            } : undefined,
          });
        }
      } catch (e) {
        // ping/pong or non-JSON
      }
    };

    ws.onclose = () => {
      console.log("Notification WS disconnected, reconnecting...");
      // Backoff exponencial (5s, 10s, 20s... tope 60s) para no martillear el
      // endpoint/edge cada 5s si el WS está caído de forma sostenida (ej.
      // proxy/Cloudflare no soporta upgrade en este entorno).
      const attempt = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = attempt;
      const delay = Math.min(60000, 5000 * Math.pow(2, attempt - 1));
      reconnectRef.current = setTimeout(() => {
        if (user) connectWs();
      }, delay);
    };

    ws.onerror = () => {
      ws.close();
    };

    wsRef.current = ws;
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      connectWs();
    }
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [user, fetchNotifications, connectWs]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (e) {
      console.error(e);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markRead, markAllRead, fetchNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
